/* eslint-disable no-else-return */
import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { UniqueKeys } from '@/types'
import type LoggerService from '@/services/loggerService'
import type ResolverManager from '@/services/resolvers/resolverManager'
import type { PromiseExecutorPoolManager } from '@/services/promiseExecutorPoolManager'
import type { ResolverConfig } from '@/services/types'
import type { Route } from '@/services/route/types'
import type { LoggerChannels } from '@/config/configDev'
import type { ResolverExecutorServiceInterface } from '@/services/resolvers/types'

const enum ResolverExecutorPoolItemType {
  Resolve,
  Unresolve,
}

@injectable()
export class ResolverExecutorService implements ResolverExecutorServiceInterface {
  private readonly resolvePromiseKey = Symbol('resolver:resolve')

  private readonly unresolvePromiseKey = Symbol('resolver:unresolve')

  constructor(
    @inject(SERVICE_TYPES.PromiseExecutorPoolManager) protected readonly poolManager: PromiseExecutorPoolManager,
    @inject(SERVICE_TYPES.ResolverManager) protected readonly resolverManager: ResolverManager,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
  ) {}

  public async resolve(config: ResolverConfig, route?: Route, fromRoute?: Route): Promise<unknown> {
    this.log('Resolver execution requested…', config)

    const resolver = this.getResolver(config)
    const resolverKeys: UniqueKeys = resolver.getResolverKeys?.(config.params, route, fromRoute) ?? []
    const hasResolverKeys = resolverKeys.length > 0

    if (!hasResolverKeys) {
      this.log('Resolver keys is empty, resolving the default way')
      return resolver.resolve(config.params, route, fromRoute)
    } else {
      const resolvePromiseKeys = this.getPromiseKeys(ResolverExecutorPoolItemType.Resolve, resolverKeys)

      // Persistent resolvers
      if (resolver.isPersistent?.()) {
        this.log('It is a persistent resolver')
        let promise = this.poolManager.getPromiseFromPool(resolvePromiseKeys)
        if (!promise) {
          this.log('Resolver is not resolving yet, creating new promise')
          promise = resolver.resolve(config.params, route, fromRoute)
        } else {
          this.log('Resolver is already resolving, returning existing promise and incrementing the count in the pool')
        }
        this.poolManager.addPromiseToPool(resolvePromiseKeys, promise)
        return promise
      }

      // Regular resolvers
      if (this.poolManager.hasPromiseInPool(resolvePromiseKeys)) {
        this.log('Resolver already resolving, returning existing promise')
        return this.poolManager.getPromiseFromPool(resolvePromiseKeys)
      } else {
        this.log('Resolver is not resolving yet, creating new promise')
        const promise = resolver.resolve(config.params, route, fromRoute)
        this.poolManager.addPromiseToPool(resolvePromiseKeys, promise)
        promise.finally(() => {
          this.log('Promise resolve is fulfilled, removing from pool', config)
          this.poolManager.removePromiseFromPool(resolvePromiseKeys)
        })
        return promise
      }
    }
  }

  public async unresolve(config: ResolverConfig, route?: Route, fromRoute?: Route): Promise<unknown> {
    this.log('Resolver unresolve requested…', config)
    const resolver = this.getResolver(config)

    const resolverKeys: UniqueKeys = resolver.getResolverKeys?.(config.params, route, fromRoute) ?? []
    const hasResolverKeys = resolverKeys.length > 0

    if (!hasResolverKeys) {
      this.log('Resolver keys is empty, unresolving the default way')
      return resolver.unresolve ? resolver.unresolve(config.params, route, fromRoute) : Promise.resolve()
    } else {
      const unresolvePromiseKeys = this.getPromiseKeys(ResolverExecutorPoolItemType.Unresolve, resolverKeys)

      // Persistent resolvers
      if (resolver.isPersistent?.()) {
        this.log('It is a persistent resolver')
        const resolvePromiseKeys = this.getPromiseKeys(ResolverExecutorPoolItemType.Resolve, resolverKeys)

        if (this.poolManager.hasPromiseInPool(unresolvePromiseKeys)) {
          this.log('Resolver already unresolving, returning existing promise')
          return this.poolManager.getPromiseFromPool(unresolvePromiseKeys)
        }

        // Wait for microtask to allow pending resolve to increment the pool count first
        // This fixes race condition when unresolve is called before resolve during route transitions
        await Promise.resolve()

        if (this.poolManager.getPromiseCountInPool(resolvePromiseKeys) > 1) {
          this.log('Resolver is not a root of persistent resolvers, decrementing the count in the pool')
          this.poolManager.removePromiseFromPool(resolvePromiseKeys)
          return Promise.resolve()
        }
        // Unresolve only if it is a root of persistent resolvers
        const promise = resolver.unresolve ? resolver.unresolve(config.params, route, fromRoute) : Promise.resolve()
        this.poolManager.addPromiseToPool(unresolvePromiseKeys, promise)
        promise.finally(() => {
          this.log('Promise unresolve is fulfilled, removing from pool', config)
          this.poolManager.removePromiseFromPool(unresolvePromiseKeys)
          this.poolManager.removePromiseFromPool(resolvePromiseKeys)
        })
        return promise
      }

      // Regular resolvers
      if (this.poolManager.hasPromiseInPool(unresolvePromiseKeys)) {
        this.log('Resolver already unresolving, returning existing promise')
        return this.poolManager.getPromiseFromPool(unresolvePromiseKeys)
      } else {
        this.log('Resolver is not unresolving yet, creating new promise')

        const promise = resolver.unresolve ? resolver.unresolve(config.params, route, fromRoute) : Promise.resolve()
        this.poolManager.addPromiseToPool(unresolvePromiseKeys, promise)

        promise.finally(() => {
          this.log('Promise unresolve is fulfilled, removing from pool', config)
          this.poolManager.removePromiseFromPool(unresolvePromiseKeys)
        })

        return promise
      }
    }
  }

  public cleanup() {
    this.poolManager.cleanup()
  }

  protected getResolver(config: ResolverConfig) {
    return this.resolverManager.getResolver(config.service)
  }

  protected getPromiseKeys(type: ResolverExecutorPoolItemType, resolverKeys: UniqueKeys) {
    return [
      type === ResolverExecutorPoolItemType.Resolve ? this.resolvePromiseKey : this.unresolvePromiseKey,
      ...resolverKeys,
    ]
  }

  private log(message: string, config?: ResolverConfig) {
    const channel: LoggerChannels = 'resolver'
    this.loggerService.log(channel, message, 'ResolverExecutorService')
    if (config) {
      this.loggerService.raw(channel, config)
    }
  }
}
